/**
 * InputScriptApi — the `engine.input` capability module (global service).
 *
 * Action-addressed input (the canonical form): analog axis + pressed/down/up edges. The
 * backing is the runtime InputManager (injected per runtime; identical shape to the legacy
 * `InputCaps`, so the existing wiring serves as the backing unchanged — step 4d).
 */

import { scriptApi, scriptFn } from "../scriptApi";

/** The injected input backing (InputManager-backed at runtime; structurally = InputCaps). */
export interface InputBacking {
  getInput(action: string): number;
  isInputPressed(action: string): boolean;
  isInputDown(action: string): boolean;
  isInputUp(action: string): boolean;
  /** Vector value of an `axis2d` action (WASD composite, `<Mouse>/position`, stick). Optional. */
  getInputVector?(action: string): { x: number; y: number };
}

const VEC_ZERO = { x: 0, y: 0 };

@scriptApi("input", { tier: "safe", version: 1 })
export class InputScriptApi {
  constructor(private readonly _backing: InputBacking) {}

  /** Analog/axis value for an action, typically in [-1, 1] or [0, 1]. */
  @scriptFn() getInput(action: string): number {
    return this._backing.getInput(action);
  }

  /** Held this frame. */
  @scriptFn() isInputPressed(action: string): boolean {
    return this._backing.isInputPressed(action);
  }

  /** Went down this frame. */
  @scriptFn() isInputDown(action: string): boolean {
    return this._backing.isInputDown(action);
  }

  /** Went up this frame. */
  @scriptFn() isInputUp(action: string): boolean {
    return this._backing.isInputUp(action);
  }

  /**
   * Vector value of an `axis2d` action — the action-addressed read for composites
   * (WASD/stick) and pointer bindings (`<Mouse>/position`, in client/screen coords;
   * convert via `engine.camera.screenToWorld`). {0,0} when unbound or scalar.
   */
  @scriptFn() getInputVector(action: string): { x: number; y: number } {
    return this._backing.getInputVector?.(action) ?? VEC_ZERO;
  }
}
